import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StorageKey } from '../storage/models/storage-key.enum';
import { firstValueFrom, timeout } from 'rxjs';
import { SecureStorageService } from '../storage/secure-storage.service';
import { PromptService } from '../promtp-service/prompt.service';
import { Browser } from '@capacitor/browser';
import { VirtualHealthLibraryApiConfiguration } from 'src/app/api/proxy/virtualHealthLibrary/virtual-health-library-api-configuration';
import {
  mediaCategoryListGet,
  mediaLatestMediaGet,
  mediaSearchGet,
  mediaMediaItemUrlGet,
  mediaMediaItemRawGet,
  aiMediaQueryPost,
  aiMiniAiChatQueryPost,
  aiResponseRelevantPost,
  aiArticleReadPost
} from 'src/app/api/proxy/virtualHealthLibrary/functions';
import { MediaCategoryModel, MediaHtmlResponse, MediaItemModel, MediaQueryResponse, MiniAiChatQueryResponse } from 'src/app/api/proxy/virtualHealthLibrary/virtual-health-library-models';
import { LoadingService } from '../loading-service/loading.service';
import { aiMiniAiChatQueryPost as aiMiniAiChatQueryPostFn } from 'src/app/api/proxy/virtualHealthLibrary/fn/ai/ai-mini-ai-chat-query-post';
import { aiMediaQueryPost as aiMediaQueryPostFn } from 'src/app/api/proxy/virtualHealthLibrary/fn/ai/ai-media-query-post';
import { Network } from '@capacitor/network';
import { TranslatorService } from '../translator-service/translator.service';
import { EventService } from '../events/event.service';
import { VivaBotFaceState } from 'src/app/controls/viva-chat/viva-chat.component';
import { BaseApiService } from '../base-api-service/base-api.service';

/** Max successful MediaQuery / MiniAiChatQuery request+response pairs kept for exact-match reuse. */
const AI_QUERY_CACHE_LIMIT = 10;

@Injectable({
  providedIn: 'root',
})
export class MediaService extends BaseApiService {
  public NETWORK_TIMEOUT = 60000;
  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private virtualHealthLibraryCfg: VirtualHealthLibraryApiConfiguration,
    private _TranslatorService: TranslatorService,
  ) {
    super(_PromptService);
    LoadingService.addSilentUrl(aiMiniAiChatQueryPostFn.PATH);
    LoadingService.addSilentUrl(aiMediaQueryPostFn.PATH);
  }


  public async categoryListAsync(): Promise<MediaCategoryModel[] | null> {
    const mediaCategories = await SecureStorageService.get(StorageKey.MediaCategories);
    let model: MediaCategoryModel[] | null = null;
    if (mediaCategories) {
      try {
        model = JSON.parse(mediaCategories);
        if (model && model.length == 0) {
          model = null;
        }
      } catch {
        model = null
      }
    }

    const obs = mediaCategoryListGet(this.http, this.virtualHealthLibraryCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'categoryListAsync');
    const result = httpResponse ? httpResponse.body : null;
    if (result) {
      await SecureStorageService.set(StorageKey.MediaCategories, JSON.stringify(result));
      return result;
    }
    return model;
  }

  public async latestMediaAsync(numArticles: number): Promise<MediaItemModel[] | null> {
    const lastesMedia = await SecureStorageService.get(StorageKey.LatestArticles);
    let model: MediaItemModel[] | null = null;
    if (lastesMedia) {
      try {
        model = JSON.parse(lastesMedia);
      } catch {
        model = null
      }
    }


    const obs = mediaLatestMediaGet(this.http, this.virtualHealthLibraryCfg.rootUrl, { numberOfItems: numArticles });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'latestMediaAsync');
    const result = httpResponse ? httpResponse.body : null;
    if (result) {
      await SecureStorageService.set(StorageKey.LatestArticles, JSON.stringify(result));
      return result;
    }
    return model;


  }

  public async searchAsync(categoryId: string | undefined, keywords: string | undefined): Promise<MediaItemModel[]> {

    const obs = mediaSearchGet(this.http, this.virtualHealthLibraryCfg.rootUrl, { keyWords: keywords, categoryId: categoryId });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'searchAsync');
    const result = httpResponse ? httpResponse.body : null;
    return result ?? [];
  }

  public async viewMediaItemAsync(mediaId: string): Promise<string> {

    const obs = mediaMediaItemUrlGet(this.http, this.virtualHealthLibraryCfg.rootUrl, { mediaId: mediaId });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'viewMediaItemAsync');
    const result = httpResponse ? httpResponse.body : null;
    if (result) {
      await Browser.open({ url: result });
      return result;
    }
    return '';
  }


  public async aiQuery(thePrompt: string, webSearchOn: boolean = false): Promise<MediaQueryResponse | null> {
    await EventService.aiBotEmotionChange.emit(VivaBotFaceState.thinking);

    let language = 'english';
    const culture = this._TranslatorService.getCurrentLanguage();
    const lan = this._TranslatorService.getAvailableLanguages().find(
      (a) => a.culture === culture,
    );
    if (lan) language = lan.text;

    // Build the exact request body first so cache matches what would be sent to the server.
    const requestPrompt = thePrompt + this._TranslatorService.translateText('media-service.ai-prompt.answer-in', { language });
    const requestKey = this.buildAiRequestKey(requestPrompt, webSearchOn);

    const cached = await this.findChatCache(requestKey);
    if (cached) {
      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
      return cached.chatResponse;
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      const offline: MediaQueryResponse = {
        responseText: this._TranslatorService.translateText('media-service.errors.device-offline')
      };
      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
      return offline;
    }

    try {
      const obs = aiMediaQueryPost(this.http, this.virtualHealthLibraryCfg.rootUrl, {
        body: {
          prompt: requestPrompt,
          webSearchOn: webSearchOn
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const result = httpResponse.body!;
      if (result) {
        await this.addChatCache(requestKey, webSearchOn, result);
      }
      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
      return result;
    } catch (error) {
    }
    await EventService.aiBotEmotionChange.emit(VivaBotFaceState.sad);
    return null;
  }

  public async aiMiniQuery(thePrompt: string, webSearchOn: boolean = false): Promise<MiniAiChatQueryResponse | null> {

    let language = 'english';
    const culture = this._TranslatorService.getCurrentLanguage();
    const lan = this._TranslatorService.getAvailableLanguages().find(
      (a) => a.culture === culture,
    );
    if (lan) language = lan.text;
    const requestPrompt = thePrompt + this._TranslatorService.translateText('media-service.ai-prompt.answer-in-mini', { language });
    const requestKey = this.buildAiRequestKey(requestPrompt, webSearchOn);

    await EventService.aiBotEmotionChange.emit(VivaBotFaceState.thinking);

    const cached = await this.findMiniChatCache(requestKey);
    if (cached) {
      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
      return cached.chatResponse;
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      const offline: MediaQueryResponse = {
        responseText: this._TranslatorService.translateText('media-service.errors.device-offline')
      };

      return offline;
    }


    try {
      const obs = aiMiniAiChatQueryPost(this.http, this.virtualHealthLibraryCfg.rootUrl, {
        body: {
          prompt: requestPrompt,
          webSearchOn: webSearchOn
        }
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const result = httpResponse.body!;

      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
      if (result) {
        await this.addMiniChatCache(requestKey, webSearchOn, result);
      }

      return result;
    } catch (error) {
      await EventService.aiBotEmotionChange.emit(VivaBotFaceState.error);
    }
    return null;
  }

  /** Exact request identity for MediaQuery / MiniAiChatQuery bodies. */
  private buildAiRequestKey(prompt: string, webSearchOn: boolean): string {
    return JSON.stringify({ prompt, webSearchOn });
  }

  private filterUnexpiredCache<T extends { expires?: Date | string }>(items: T[] | null): T[] {
    if (!items || items.length === 0) {
      return [];
    }
    const now = new Date();
    return items.filter(item => {
      if (!item.expires) return true;
      return new Date(item.expires) > now;
    });
  }

  private async getMiniChatCache(): Promise<IMiniAiChatQueryCache[]> {
    const miniCache = await SecureStorageService.get(StorageKey.MiniAiChatQueryCache);
    let model: IMiniAiChatQueryCache[] | null = null;
    if (miniCache) {
      try {
        model = JSON.parse(miniCache);
      } catch {
        model = null;
      }
    }
    return this.filterUnexpiredCache(model);
  }

  private async findMiniChatCache(requestKey: string): Promise<IMiniAiChatQueryCache | null> {
    const cache = await this.getMiniChatCache();
    return cache.find(cItem => cItem.requestKey === requestKey) ?? null;
  }

  private async addMiniChatCache(requestKey: string, webSearchOn: boolean, response: MiniAiChatQueryResponse): Promise<void> {
    if (!requestKey || requestKey.length <= 2 || !response) {
      return;
    }

    let cache = await this.getMiniChatCache();
    if (cache.some(cItem => cItem.requestKey === requestKey)) {
      return;
    }

    cache.push({
      requestKey,
      webSearchOn,
      chatResponse: response,
      expires: new Date(Date.now() + 3 * 60 * 60 * 1000),
    });

    while (cache.length > AI_QUERY_CACHE_LIMIT) {
      cache.shift();
    }

    await SecureStorageService.set(StorageKey.MiniAiChatQueryCache, JSON.stringify(cache));
  }

  private async getChatCache(): Promise<IAiChatQueryCache[]> {
    const chatCache = await SecureStorageService.get(StorageKey.AiChatQueryCache);
    let model: IAiChatQueryCache[] | null = null;
    if (chatCache) {
      try {
        model = JSON.parse(chatCache);
      } catch {
        model = null;
      }
    }
    return this.filterUnexpiredCache(model);
  }

  private async findChatCache(requestKey: string): Promise<IAiChatQueryCache | null> {
    const cache = await this.getChatCache();
    return cache.find(cItem => cItem.requestKey === requestKey) ?? null;
  }

  private async addChatCache(requestKey: string, webSearchOn: boolean, response: MediaQueryResponse): Promise<void> {
    if (!requestKey || requestKey.length <= 2 || !response) {
      return;
    }

    let cache = await this.getChatCache();
    if (cache.some(cItem => cItem.requestKey === requestKey && cItem.webSearchOn === webSearchOn)) {
      return;
    }

    cache.push({
      requestKey,
      webSearchOn,
      chatResponse: response,
      expires: new Date(Date.now() + 3 * 60 * 60 * 1000),
    });

    while (cache.length > AI_QUERY_CACHE_LIMIT) {
      cache.shift();
    }

    await SecureStorageService.set(StorageKey.AiChatQueryCache, JSON.stringify(cache));
  }

  public async aiResponseRelevantAsync(aiResponseId: string, relevant: boolean): Promise<void> {

    const obs = aiResponseRelevantPost(this.http, this.virtualHealthLibraryCfg.rootUrl, {
      body: { id: aiResponseId, responseRelevant: relevant },
    });
    await this.callApi(obs, this.NETWORK_TIMEOUT, 'aiResponseRelevantAsync');
  }

  public async aaiArticleReadAsync(aiResponseId: string): Promise<void> {

    const obs = aiArticleReadPost(this.http, this.virtualHealthLibraryCfg.rootUrl, {
      body: { id: aiResponseId },
    });
    await this.callApi(obs, this.NETWORK_TIMEOUT, 'aaiArticleReadAsync');
  }

  public async getMediaArticle(mediaId: string): Promise<MediaHtmlResponse | null> {
    const obs = mediaMediaItemRawGet(this.http, this.virtualHealthLibraryCfg.rootUrl, { mediaId });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getMediaArticle');
    const articleHtml = httpResponse ? httpResponse.body : null;

    return articleHtml;
  }

}

export interface IMiniAiChatQueryCache {
  /** Exact JSON of `{ prompt, webSearchOn }` as sent to the server. */
  requestKey: string;
  webSearchOn: boolean;
  chatResponse: MiniAiChatQueryResponse;
  expires?: Date;
  /** @deprecated legacy field — ignored for lookups */
  questionTextHash?: string;
}

export interface IAiChatQueryCache {
  /** Exact JSON of `{ prompt, webSearchOn }` as sent to the server. */
  requestKey: string;
  webSearchOn: boolean;
  chatResponse: MediaQueryResponse;
  expires?: Date;
  /** @deprecated legacy field — ignored for lookups */
  questionTextHash?: string;
}
